
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { ProgressHandler } from "../types";
import { useAIDisabled } from "@/utils/settings";
import { isPdfFile } from "../cv-storage";

/**
 * Analyzes a candidate's resume using AI
 * @param candidateId - ID of the candidate
 * @param cvUrl - URL to the CV file
 * @param cvText - Text content of the CV
 * @param onProgress - Progress callback function
 * @param notes - Optional additional notes for analysis
 * @returns Analysis results
 */

export const analyzeResumeWithAI = async (
  candidateId: string,
  cvUrl: string | null,
  cvText: string,
  onProgress: ProgressHandler,
  notes: string = ""
): Promise<any> => {
  onProgress("Analyzing with AI", 80);

  try {

    // Check if AI is disabled globally
    if (useAIDisabled()) {
      console.log("AI analysis skipped: AI features are disabled in settings");
      toast({
        title: "AI Analysis Skipped",
        description: "AI features are currently disabled in settings",
        variant: "default",
      });
      return { analysis: { message: "AI analysis disabled via settings" } };
    }

    if (!cvText && cvUrl) {

      const { data, error } = await supabase.functions.invoke('get-document-content', {
        body: { documentUrl: cvUrl }
      });

      if (error) {
        console.error("Error extracting CV content:", error);
        cvText = ""; // Set empty text if extraction fails

        throw new Error("CV Content Extraction Failed");

      } else if (data && data.text) {
        cvText = data.text;
        console.log("CV content extracted successfully");
        console.log(cvText);
      }

    }

    // Determine what to send for CV content - prioritize URL for PDFs, text for other formats
    const payload = {
      candidateId: candidateId,
      cvUrl: cvUrl || null,
      cvContent: cvText || null,
      notes: notes // Pass optional notes
    };

    console.log("Calling analyse-resume function with:", {
      candidateId,
      cvUrl: payload.cvUrl,
      cvContentLength: payload.cvContent ? `${payload.cvContent.length} chars` : 'null',
      notesLength: notes ? `${notes.length} chars` : 'none',
    });

    const aiResponse = await supabase.functions.invoke('analyse-resume', {
      body: payload
    });

    if (aiResponse.error) {
      console.error("AI analysis error:", aiResponse.error);
      throw new Error(`AI Analysis Failed: ${aiResponse.error.message || 'Unknown error'}`);
    }

    console.log("AI analysis completed successfully:", aiResponse.data);
    return aiResponse.data;
  } catch (error) {
    console.error("Error during AI analysis:", error);
    throw new Error(`AI Analysis Failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};
